/**
 * Wire contract for proxy failures.
 *
 * The proxy routes used to answer with an English sentence only (`{"error":
 * "URL not allowed"}`), which the viewers had no choice but to render verbatim
 * — an English string in a Czech UI that also told the user nothing about what
 * to do next. Every failure now carries a stable `code` the client maps to a
 * localized, actionable message; the `error` string stays for logs and for
 * anything reading the API directly.
 *
 * Codes are behaviour, not HTTP status: `UPSTREAM_UNREACHABLE` (nothing
 * answered) and `UPSTREAM_NOT_MEDIA` (something answered, with a login page)
 * are both 502 but need opposite advice — 2026-08-04, a MUNI camera did each
 * on consecutive days.
 */
export const PROXY_ERROR_CODES = [
    /** The `?url=` parameter is missing or not a parseable absolute URL. */
    'INVALID_URL',
    /** Target is a private-network address; the server deliberately won't reach into LANs. */
    'PRIVATE_ADDRESS',
    /** Target is public but outside the configured trusted-host allowlist. */
    'HOST_NOT_ALLOWED',
    /** Caller spent its distinct-host budget for this window. */
    'RATE_LIMITED',
    /** The per-IP request budget of the proxy is spent; often a shared network, not this viewer. */
    'TOO_MANY_REQUESTS',
    /** The fetch never completed: connect refused, DNS failure, no route. */
    'UPSTREAM_UNREACHABLE',
    /** The source accepted the connection and then stalled past our deadline. */
    'UPSTREAM_TIMEOUT',
    /** The source answered with a non-2xx status (see `upstreamStatus`). */
    'UPSTREAM_STATUS',
    /** The source answered 2xx with something that isn't media (see `contentType`). */
    'UPSTREAM_NOT_MEDIA',
    /** The source answered 2xx and then produced no usable body at all. */
    'UPSTREAM_EMPTY',
    /** The source tried to redirect; following it would bypass the allowlist. */
    'UPSTREAM_REDIRECT_BLOCKED',
    /** The space is not public and the caller is anonymous — signing in may help. */
    'LOGIN_REQUIRED',
    /** The caller is known but not permitted (visibility, IP range, domain). */
    'ACCESS_DENIED',
    /** No service with that id. */
    'SERVICE_NOT_FOUND',
    /** The configured service exists but is switched off. */
    'SERVICE_INACTIVE',
    /** The service has no endpoint, or one that no longer parses — an admin must fix it. */
    'SERVICE_NOT_CONFIGURED',
    /** This service's format has no endpoint for the requested shape (e.g. a single frame). */
    'FORMAT_UNSUPPORTED',
    /** Our own media relay failed to take the source on. */
    'RELAY_FAILED',
    /** The relay is already transcoding its maximum number of streams. */
    'CAPACITY',
];
/**
 * i18n key per reason. Lives here rather than in the client so the mapping is
 * exhaustive by construction (`Record<ProxyErrorCode, …>` fails to compile when
 * a code is added without a message) and testable — the client package has no
 * test runner.
 *
 * Every key here is a COMPLETE sentence with no interpolation. Detail — the
 * upstream status, the content type — rides a separate `…Detail` key chosen
 * only when the value is actually present (see {@link proxyErrorTranslation}).
 * i18next runs with `skipOnVariables: true`, so a key whose placeholder has no
 * value renders the literal `{{contentType}}` to the user; keeping the
 * placeholder out of the default key makes that unreachable rather than
 * something each call site has to remember.
 */
export const PROXY_ERROR_MESSAGE_KEYS = {
    INVALID_URL: 'viewer.proxyError.invalidUrl',
    PRIVATE_ADDRESS: 'viewer.proxyError.privateAddress',
    HOST_NOT_ALLOWED: 'viewer.proxyError.hostNotAllowed',
    RATE_LIMITED: 'viewer.proxyError.rateLimited',
    TOO_MANY_REQUESTS: 'viewer.proxyError.tooManyRequests',
    UPSTREAM_UNREACHABLE: 'viewer.proxyError.upstreamUnreachable',
    UPSTREAM_TIMEOUT: 'viewer.proxyError.upstreamTimeout',
    UPSTREAM_STATUS: 'viewer.proxyError.upstreamStatus',
    UPSTREAM_NOT_MEDIA: 'viewer.proxyError.upstreamNotMedia',
    UPSTREAM_EMPTY: 'viewer.proxyError.upstreamEmpty',
    UPSTREAM_REDIRECT_BLOCKED: 'viewer.proxyError.upstreamRedirectBlocked',
    LOGIN_REQUIRED: 'viewer.proxyError.loginRequired',
    ACCESS_DENIED: 'viewer.proxyError.accessDenied',
    SERVICE_NOT_FOUND: 'viewer.proxyError.serviceNotFound',
    SERVICE_INACTIVE: 'viewer.proxyError.serviceInactive',
    SERVICE_NOT_CONFIGURED: 'viewer.proxyError.serviceNotConfigured',
    FORMAT_UNSUPPORTED: 'viewer.proxyError.formatUnsupported',
    RELAY_FAILED: 'viewer.proxyError.relayFailed',
    CAPACITY: 'viewer.proxyError.capacity',
};
/** Richer key used only when the detail value it interpolates is present. */
const PROXY_ERROR_DETAIL_KEYS = {
    UPSTREAM_STATUS: { param: 'status', key: 'viewer.proxyError.upstreamStatusDetail' },
    UPSTREAM_NOT_MEDIA: { param: 'contentType', key: 'viewer.proxyError.upstreamNotMediaDetail' },
};
/** Translation key + interpolation values for a parsed proxy error body. */
export function proxyErrorTranslation(body) {
    const params = {};
    if (body.upstreamStatus !== undefined)
        params.status = body.upstreamStatus;
    // Strip parameters (`; charset=utf-8`): the bare type is the useful part and
    // the full header reads like noise inside a sentence.
    if (body.contentType)
        params.contentType = body.contentType.split(';')[0].trim();
    const detail = body.code in PROXY_ERROR_DETAIL_KEYS
        ? PROXY_ERROR_DETAIL_KEYS[body.code]
        : undefined;
    if (detail && params[detail.param] !== undefined) {
        return { key: detail.key, params };
    }
    return { key: PROXY_ERROR_MESSAGE_KEYS[body.code], params };
}
export function isProxyErrorCode(value) {
    return typeof value === 'string' && PROXY_ERROR_CODES.includes(value);
}
/** Build the JSON body a proxy route hands back, dropping unset detail fields. */
export function proxyErrorBody(code, error, detail = {}) {
    return {
        error,
        code,
        ...(typeof detail.upstreamStatus === 'number' ? { upstreamStatus: detail.upstreamStatus } : {}),
        ...(typeof detail.contentType === 'string' ? { contentType: detail.contentType } : {}),
    };
}
/**
 * Read a proxy error body. Returns `null` for anything without a recognised
 * code — an unknown shape must fall back to the client's generic message
 * rather than leak a server string into the UI. Detail fields of the wrong
 * type are dropped rather than passed through to a translation.
 */
export function parseProxyErrorBody(body) {
    if (typeof body !== 'object' || body === null || Array.isArray(body))
        return null;
    const candidate = body;
    if (!isProxyErrorCode(candidate.code))
        return null;
    return proxyErrorBody(candidate.code, typeof candidate.error === 'string' ? candidate.error : '', {
        upstreamStatus: typeof candidate.upstreamStatus === 'number' ? candidate.upstreamStatus : undefined,
        contentType: typeof candidate.contentType === 'string' ? candidate.contentType : undefined,
    });
}
/**
 * How long a refused connect to an upstream takes to reach the viewer as a 502 on
 * `/api/proxy/service/:id`. That route gives its upstream fetch no signal, so a
 * host that drops packets fails only at undici's default connect timeout (10 s;
 * measured 10.5 s on 3. 11. 2026 with `UND_ERR_CONNECT_TIMEOUT`).
 */
export const PROXY_UPSTREAM_CONNECT_TIMEOUT_MS = 10_000;
/**
 * How long a viewer waits for the proxy to say why a media element failed. It
 * must outlast {@link PROXY_UPSTREAM_CONNECT_TIMEOUT_MS} with room for the network,
 * or a powered-off camera ends as the generic "cannot load" instead of "the source
 * does not answer". A source that accepts and never answers is still cut off here.
 */
export const PROXY_PROBE_TIMEOUT_MS = 15_000;
/** Used when a 429 has no readable `Retry-After`. */
const DEFAULT_THROTTLE_MS = 10_000;
/** The limiter's window is 60 s, so no answer should park a viewer for longer. */
const MAX_THROTTLE_MS = 60_000;
function throttleMs(retryAfter) {
    const seconds = Number(retryAfter);
    if (!retryAfter || !Number.isFinite(seconds) || seconds <= 0)
        return DEFAULT_THROTTLE_MS;
    return Math.min(seconds * 1000, MAX_THROTTLE_MS);
}
/**
 * Classify a proxy response for a viewer. A 429 is reported as such even with an
 * unreadable body, because it can come from something other than the current
 * limiter, and the generic "cannot load image" is exactly what MU reported.
 */
export function classifyProxyResponse(response) {
    if (response.ok)
        return { kind: 'ok' };
    const body = parseProxyErrorBody(response.body);
    if (response.status === 429) {
        return {
            kind: 'refused',
            body: body ?? proxyErrorBody('TOO_MANY_REQUESTS', 'Too many requests'),
            retryAfterMs: throttleMs(response.retryAfter),
        };
    }
    return body ? { kind: 'refused', body } : { kind: 'unknown' };
}
//# sourceMappingURL=proxy-errors.js.map